"use client";

import { useSearch } from "@/context/SearchContext";
import { useLocale } from "@/context/LocaleContext";
import { Branding } from "@/types/branding";
import { Journal } from "@/types/journal";
import { I18nData } from "@/types/i18n";
import LoadingRow from "./loading-row";
import "./results-table.scss";
import Row from "./row";
import TableHeader from "./table-header";

interface TableProps {
  branding: Branding | null;
  i18nData: I18nData;
}

function TableContent({
  data,
  i18nData,
  loading,
  branding,
}: {
  data: Journal[];
  i18nData: I18nData;
  loading: boolean;
  branding: Branding | null;
}) {
  return (
    <tbody id="resultsTableBody">
      {loading ? (
        <LoadingRow i18nData={i18nData} />
      ) : (
        data?.map((journal) => (
          <Row key={journal._id} journal={journal} i18nData={i18nData} branding={branding} />
        ))
      )}
    </tbody>
  );
}

export default function ResultsTable({ branding, i18nData }: TableProps) {
  const { searchResult, loading, currentSearchTerm } = useSearch();
  const { locale } = useLocale();

  const statusMessage = loading
    ? i18nData[locale]?.loading
    : searchResult
    ? searchResult.total_count > 0
      ? i18nData[locale]?.results_found
          ?.replace('{{count}}', searchResult.total_count.toString())
          ?.replace('{{term}}', currentSearchTerm || '')
      : i18nData[locale]?.no_results_found
          ?.replace('{{term}}', currentSearchTerm || '')
    : "";

  return (
    <>
      {/* Accessibility: Live region for screen readers. 
      Announces loading without needing focus. 
      Required to meet WCAG 2.1 - Success Criterion 4.1.3 (Status Messages). */}
      <div
        role="status"
        aria-live="polite"
        aria-atomic="true"
        className="sr-only"
      >
        {statusMessage}
      </div>
      <table>
        <TableHeader paymentTitle={branding?.payment_title} i18nData={i18nData} />
        <TableContent
          data={searchResult?.data ?? []}
          i18nData={i18nData}
          loading={loading}
          branding={branding}
        />
      </table>
    </>
  );
}
